import { VideoEditor } from '../VideoEditor';
import { Element } from '../models/Element';
import { Track } from '../models/Track';
import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { TimelineMode } from './TimelineMode';
import { VideoElement } from '../models';
import { AbstractLayoutCommand } from './AbstractLayoutCommand';
import { AddLayoutElementsCommand } from './AddLayoutElementsCommand';
import { ClearLayoutElementsCommand } from './ClearLayoutElementsCommand';

export interface MergeLayoutElementsParameters {
  prev: Element;
  next: Element;
  track: Track;
  mode?: TimelineMode;
}

export class MergeLayoutElementsCommand extends AbstractLayoutCommand {
  private params: MergeLayoutElementsParameters;

  constructor(
    editor: VideoEditor,
    element: Element | undefined,
    params: MergeLayoutElementsParameters,
  ) {
    super(editor, element || params.prev || params.next);
    this.params = params;
  }

  execute(): boolean {
    if (!this.params.prev || !this.params.next) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Missing required elements for merge',
      });
      return false;
    }

    super.execute(); // makes a snapshot

    const { prev, next, track } = this.params;

    if (
      this.extraElementData.isLayoutElement(prev) &&
      this.extraElementData.isLayoutElement(next)
    ) {
      // both are layout elements, can merge
      const success = this.mergeLayoutElements(prev, next, track);
      if (!success) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: 'Failed to merge layout elements',
        });
        return false;
      }
    } else if (
      this.extraElementData.isLayoutElement(prev) ||
      this.extraElementData.isLayoutElement(next)
    ) {
      // one of the elements is not a layout element, drop stacked layout mode and merge parents
      this.mergeAndForgetLayoutElements(prev, next, track);
    } else {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to merge layout elements: no layout elements found',
      });
      return false;
    }
    this.canUndo = true;

    return true;
  }

  getDescription(): string {
    return `Merged elements ${this.params.prev.id} and ${this.params.next.id}`;
  }

  /**
   * Assumes both prev and next are layout elements, works by deleting children.
   * Merges the parents and re-creates layout children
   * @param prev The previous element
   * @param next The next element
   * @param track The track to merge the elements into
   * @returns True if the merge was successful, false otherwise
   */
  private mergeLayoutElements(
    prev: Element,
    next: Element,
    track: Track,
  ): boolean {
    const prevLayoutElements = this.pickAllLayoutElements(prev);
    const nextLayoutElements = this.pickAllLayoutElements(next);

    if (!prevLayoutElements || !nextLayoutElements) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Could not find layout elements during merge',
      });
      return false;
    }

    const childrenToDelete = [
      ...prevLayoutElements.children,
      ...nextLayoutElements.children,
    ];

    for (let i = 0; i < childrenToDelete.length; i++) {
      const child = childrenToDelete[i];

      const success = this.editor
        .getTrackManager()
        .removeElement(child, TimelineMode.DEFAULT);

      if (success) {
        this.extraElementData.removeElementData(child.id);
      }
    }

    const mergedElement = this.editor
      .getTrackManager()
      .bridgeAndMergeElements(
        [prevLayoutElements.parent, nextLayoutElements.parent],
        track,
      );

    if (!mergedElement) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Could not merge parent elements',
      });
      return false;
    }

    const layoutType =
      this.extraElementData.getElementData(prevLayoutElements.parent)
        ?.layoutType ||
      this.extraElementData.getElementData(nextLayoutElements.parent)
        ?.layoutType ||
      'stacked-1-1';

    this.extraElementData.clearLayoutData(prevLayoutElements.parent);
    this.extraElementData.clearLayoutData(nextLayoutElements.parent);

    const command = new AddLayoutElementsCommand(this.editor, mergedElement, {
      layoutType: layoutType as 'stacked-1-1' | 'stacked-1-3',
    });
    const success = command.execute();
    if (!success) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Merge: could not add layout elements',
      });
      return false;
    }

    return true;
  }

  /**
   * Assumes that one of the elements is a layout element and the other is not.
   * Drops stacked layout mode and merges the elements
   * @param prev The previous element
   * @param next The next element
   * @param track The track to merge the elements into
   * @returns True if the merge was successful, false otherwise
   */
  private mergeAndForgetLayoutElements(
    prev: Element,
    next: Element,
    track: Track,
  ): boolean {
    let prevToMerge = prev;
    let nextToMerge = next;

    if (this.extraElementData.isLayoutElement(prev)) {
      const prevLayoutElements = this.pickAllLayoutElements(prev);
      if (!prevLayoutElements) {
        VideoEditorLogger.logCommand(this, 'error', {
          error:
            'Could not pick previous layout elements during merge and forget',
        });
        return false;
      }
      prevToMerge = prevLayoutElements.parent;
      const command = new ClearLayoutElementsCommand(this.editor, prev, {
        fit: ((next as VideoElement).fit || 'cover') as
          | 'cover'
          | 'contain'
          | 'fill',
      });
      const success = command.execute();
      if (!success) {
        VideoEditorLogger.logCommand(this, 'error', {
          error:
            'Could not clear previous layout elements during merge and forget',
        });
        return false;
      }
    }

    if (this.extraElementData.isLayoutElement(next)) {
      const nextLayoutElements = this.pickAllLayoutElements(next);
      if (!nextLayoutElements) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: 'Could not pick next layout elements during merge and forget',
        });
        return false;
      }
      nextToMerge = nextLayoutElements.parent;
      const command = new ClearLayoutElementsCommand(this.editor, next, {
        fit: ((prev as VideoElement).fit || 'cover') as
          | 'cover'
          | 'contain'
          | 'fill',
      });
      const success = command.execute();
      if (!success) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: 'Could not clear next layout elements during merge and forget',
        });
        return false;
      }
    }

    const mergedElement = this.editor
      .getTrackManager()
      .bridgeAndMergeElements([prevToMerge, nextToMerge], track);
    if (!mergedElement) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Could not merge elements',
      });
      return false;
    }

    return true;
  }
}

export default MergeLayoutElementsCommand;
